// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

import { unwrap } from "@granite/api-client";
import { passwordAcceptable } from "@granite/core";
import { Button, TextField } from "@granite/ui";
import { useMutation } from "@tanstack/react-query";
import { getRouteApi, Link } from "@tanstack/react-router";
import { useState, type SubmitEvent } from "react";

import { AuthLayout, FormAlert, linkClass } from "../components/AuthLayout.js";
import { PasswordStrength } from "../components/PasswordStrength.js";
import { errorMessage, fieldErrors } from "../lib/errors.js";
import { t } from "../lib/i18n.js";
import { authClient, forgetSession } from "../lib/session.js";

const route = getRouteApi("/reset-password");

/** Without a token: ask for a reset link. With one (from the email): set the new password. */
export function ResetPasswordPage() {
  const { token } = route.useSearch();
  return token ? <NewPasswordForm token={token} /> : <RequestResetForm />;
}

const backToSignIn = (
  <Link to="/login" className={linkClass}>
    {t("common.backToSignIn")}
  </Link>
);

function RequestResetForm() {
  const [email, setEmail] = useState("");
  const mutation = useMutation({
    mutationFn: async (address: string) => {
      unwrap(await authClient.POST("/v1/auth/password-reset", { body: { email: address } }));
    },
  });

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    mutation.mutate(email.trim());
  };

  return (
    <AuthLayout title={t("reset.title")} footer={backToSignIn}>
      {mutation.isSuccess ? (
        <FormAlert tone="success">{t("reset.sent", { email: mutation.variables })}</FormAlert>
      ) : (
        <>
          {mutation.error && <FormAlert tone="error">{errorMessage(mutation.error)}</FormAlert>}
          <p className="mb-4 text-sm text-fg-muted">{t("reset.intro")}</p>
          <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
            <TextField
              label={t("common.email")}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
            />
            <Button type="submit" loading={mutation.isPending}>
              {t("reset.submit")}
            </Button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}

function NewPasswordForm({ token }: { token: string }) {
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [mismatch, setMismatch] = useState(false);
  const [weak, setWeak] = useState(false);
  const mutation = useMutation({
    mutationFn: async () => {
      unwrap(await authClient.POST("/v1/auth/password-reset/confirm", { body: { token, password } }));
    },
    // The server signs out every device, this browser included.
    onSuccess: forgetSession,
  });
  const fields = fieldErrors(mutation.error);

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    // The server refuses it too (also when it is built from the person's name or email).
    if (!passwordAcceptable(password)) {
      setWeak(true);
      return;
    }
    if (password !== repeat) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    mutation.mutate();
  };

  return (
    <AuthLayout title={t("reset.newTitle")} footer={mutation.isSuccess ? undefined : backToSignIn}>
      {mutation.isSuccess ? (
        <>
          <FormAlert tone="success">{t("reset.done")}</FormAlert>
          <Button asChild>
            <Link to="/login">{t("common.signIn")}</Link>
          </Button>
        </>
      ) : (
        <>
          {mutation.error && (
            <FormAlert tone="error">
              {errorMessage(mutation.error)}{" "}
              <Link to="/reset-password" search={{}} className={linkClass}>
                {t("verify.requestNew")}
              </Link>
            </FormAlert>
          )}
          <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
            <TextField
              label={t("reset.newPassword")}
              description={t("signup.passwordHint")}
              type="password"
              autoComplete="new-password"
              minLength={10}
              required
              value={password}
              error={weak ? t("password.tooWeak") : fields.password}
              onChange={(e) => {
                setPassword(e.target.value);
                setWeak(false);
              }}
            />
            <PasswordStrength password={password} />
            <TextField
              label={t("reset.confirmPassword")}
              type="password"
              autoComplete="new-password"
              required
              value={repeat}
              error={mismatch ? t("reset.mismatch") : undefined}
              onChange={(e) => {
                setRepeat(e.target.value);
              }}
            />
            <Button type="submit" loading={mutation.isPending}>
              {t("reset.save")}
            </Button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
